import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import NotificationItem from "../../Components/NotificaitonItem";
import ContactInfoIcons from "../../Components/NotificaitonItem/ContactInfoIcons";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSocketContext } from "../../Context/SocketContext/SocketProvider";
import { AppContext } from "../../Context/AppContext";
import { useSnackbar } from "../../Components/SnackBar";
import {
  NOTIFICATION_TYPE_ID,
  NOTIFICATION_TYPE_META,
} from "../../Constants/notificationTypes";
import { apiGetRequest, apiPostRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import { MEMBERS_PLATFORM_URL } from "../../Constants/config";
import emitter from "../../Utils/events/eventBus";

// How long an item must stay visible in the panel before it counts as "seen"
const AUTO_READ_SEEN_MS = 1500;

/**
 * Builds the CTA route with query params from the notification data.
 * Mirrors members buildDynamicRoute, adapted to this app's routes.
 */
function buildCtaRoute(baseRoute, notification) {
  const { notification_id, param2, param5 } = notification;
  const url = new URL(baseRoute, "http://placeholder");

  switch (notification_id) {
    case NOTIFICATION_TYPE_ID.NEW_PRICELIST_OFFERED: // 6
    case NOTIFICATION_TYPE_ID.SUPPLIER_OFFERING_TO_JOIN: // 9
      // p2 = supplier name — my-suppliers can locate & open the offer modal
      if (param2) url.searchParams.set("supplierName", param2);
      break;
    case NOTIFICATION_TYPE_ID.SERVICE_ADDED: // 2
    case NOTIFICATION_TYPE_ID.VARIANT_ADDED: // 31
      if (param2) url.searchParams.set("serviceName", param2);
      break;
    case NOTIFICATION_TYPE_ID.PRICELIST_ADDED: // 30
      if (param5) url.searchParams.set("pricelistName", param5);
      break;
    case NOTIFICATION_TYPE_ID.PAYMENT_REQUESTED: {
      // 28 — deep link into the members-app order modal at the payment step
      // (step 8 = WAITING_FOR_PAYMENT in the members order flow).
      const orderId = notification.display_params?.order_id;
      const categoryId = notification.display_params?.category_id;
      if (orderId) {
        url.searchParams.set("orderId", orderId);
        url.searchParams.set("step", "8");
        if (categoryId != null)
          url.searchParams.set("pricelist", String(categoryId));
      }
      break;
    }
    default:
      break;
  }

  return url.pathname + url.search;
}

/** Builds the contact-info lookup params for contact_info CTAs. */
function getContactLookup(notification) {
  const notifId = notification.notification_id;
  const displayParams = notification.display_params ?? {};
  const lookup = {
    communityId: displayParams.community_id,
    supplierProfileId: displayParams.supplier_profile_id,
  };

  if (
    notifId === NOTIFICATION_TYPE_ID.PAUSED_BY_CM ||
    notifId === NOTIFICATION_TYPE_ID.REMOVED_FROM_COMMUNITY
  ) {
    // Supplier needs CM contact — community name is in param2
    return { ...lookup, contactType: "cm", communityName: notification.param2 };
  }
  if (notifId === NOTIFICATION_TYPE_ID.SUPPLIER_LEFT_COMMUNITY) {
    // CM needs supplier contact — supplier name is in param1 (ID 8)
    return {
      ...lookup,
      contactType: "supplier",
      supplierName: notification.param1,
    };
  }
  // 5 / 24 / 25 — supplier contact, supplier name is in param2
  return {
    ...lookup,
    contactType: "supplier",
    supplierName: notification.param2,
  };
}

function Notifications(props) {
  const { isBelowMd, handleNotificationDrawerClose } = props;
  // Load both the template namespace (auto-generated) and the UI-chrome
  // namespace (hand-maintained) so UI keys resolve via the "notifications-ui:"
  // prefix while templates.* stay on the default "notifications" namespace.
  const { t: tPanel, i18n } = useTranslation(["notifications", "notifications-ui"]);
  const isHebrew = i18n.language === "he";
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  // Template-scoped translator passed down to items
  const t = tPanel;

  const { sidebarData, isCommunityManagementRole } = useContext(AppContext);
  const hasCommunityManagementAccess =
    sidebarData?.isCommunityManager || sidebarData?.isModerator;
  const hasSupplierAccess = sidebarData?.isSupplier;

  const {
    readIds,
    markNotificationRead,
    markNotificationUnread,
    markAllNotificationsRead,
    unreadNotificationsCount,
    historyItems,
    hasMoreHistory,
    isHistoryLoading,
    historyLoaded,
    loadNotificationsHistory,
  } = useSocketContext();

  // (Re)load the first history page whenever the panel opens
  useEffect(() => {
    loadNotificationsHistory({ reset: true });
  }, [loadNotificationsHistory]);

  // Infinite scroll: load the next page when the bottom sentinel becomes
  // visible inside the panel's scroll container.
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMoreHistory) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadNotificationsHistory();
      },
      { root: listRef.current, rootMargin: "120px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreHistory, loadNotificationsHistory]);

  // Auto-mark items as read once they've been visible in the open panel for
  // a moment ("seen" behavior). Items the user explicitly toggled back to
  // unread are exempt so they don't immediately re-mark themselves.
  const manuallyUnreadRef = useRef(new Set());
  const historyItemsRef = useRef(historyItems);
  historyItemsRef.current = historyItems;
  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return undefined;
    const timers = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.dataset.recipientId;
          if (!id) return;
          if (entry.isIntersecting) {
            if (timers.has(id) || manuallyUnreadRef.current.has(id)) return;
            timers.set(
              id,
              setTimeout(() => {
                timers.delete(id);
                const item = historyItemsRef.current.find((n) => n.id === id);
                if (item && !manuallyUnreadRef.current.has(id)) {
                  markNotificationRead(item);
                }
              }, AUTO_READ_SEEN_MS),
            );
          } else {
            const timer = timers.get(id);
            if (timer) {
              clearTimeout(timer);
              timers.delete(id);
            }
          }
        });
      },
      { root: listEl, threshold: 0.5 },
    );
    listEl
      .querySelectorAll(".notification-item")
      .forEach((el) => observer.observe(el));
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      observer.disconnect();
    };
  }, [historyItems, markNotificationRead]);

  const handleToggleRead = (item, nextRead) => {
    if (nextRead) {
      manuallyUnreadRef.current.delete(item.id);
      markNotificationRead(item);
    } else {
      manuallyUnreadRef.current.add(item.id);
      markNotificationUnread(item);
    }
  };

  const handleMarkAllRead = () => {
    manuallyUnreadRef.current.clear();
    markAllNotificationsRead();
  };

  const closePanel = useCallback(() => {
    handleNotificationDrawerClose?.();
  }, [handleNotificationDrawerClose]);

  // i18n key (under "notifications-ui") for the human-readable name of each
  // gated role, used in the "you need to be X" snackbar.
  const ROLE_NAME_KEY = {
    cm: "roleNameCommunityManager",
    supplier: "roleNameSupplier",
  };

  /**
   * Compares `receiverRole` against the currently active role view:
   *  - null: roles match (or the CTA isn't role-gated) — navigate normally
   *  - "switch": roles mismatch, but the user has access to the other role
   *    — toggle then navigate
   *  - "blocked": roles mismatch and the user doesn't have that role at all
   *    — show the "you need to be X" snackbar instead of navigating
   */
  const getRoleMismatch = (receiverRole) => {
    if (receiverRole === "cm" && !isCommunityManagementRole) {
      return hasCommunityManagementAccess ? "switch" : "blocked";
    }
    if (receiverRole === "supplier" && isCommunityManagementRole) {
      return hasSupplierAccess ? "switch" : "blocked";
    }
    return null;
  };

  const showRoleRequiredSnackbar = (receiverRole) => {
    showSnackbar(
      t("notifications-ui:roleRequired", {
        role: t(`notifications-ui:${ROLE_NAME_KEY[receiverRole]}`),
      }),
      "error",
    );
  };

  /**
   * Toggles the active role on the server, then does a full page navigation
   * (not SPA `navigate`) to `route`. A hard reload is required: the role
   * routes array in Routes/index.js is picked from localStorage at render
   * time, which only gets refreshed by AppContext's fetch-on-mount effect —
   * mirrors the existing role switch in SettingsContent/index.jsx.
   */
  const switchRoleAndNavigate = async (route) => {
    try {
      await apiPostRequest(endpoints.toggleAdminSupplierRole);
    } catch (error) {
      console.error("Failed to switch role for notification CTA:", error);
    }
    window.location.href = route;
  };

  const handleCtaClick = (notification) => {
    const meta = NOTIFICATION_TYPE_META[notification.notification_id];
    if (!meta) return;

    if (meta.ctaAction === "navigate" && meta.ctaRoute) {
      const route = buildCtaRoute(meta.ctaRoute, notification);
      const mismatch = getRoleMismatch(meta.receiverRole);
      if (meta.isMembersApp) {
        window.open(`${MEMBERS_PLATFORM_URL}${route}`, "_blank");
      } else if (mismatch === "switch") {
        switchRoleAndNavigate(route);
      } else if (mismatch === "blocked") {
        showRoleRequiredSnackbar(meta.receiverRole);
      } else {
        navigate(route);
        closePanel();
      }
    } else if (meta.ctaAction === "dynamic") {
      // Manual/system sends carry their destination in display_params.cta_url
      const ctaUrl = notification.display_params?.cta_url;
      if (ctaUrl) {
        const mismatch = getRoleMismatch(meta.receiverRole);
        if (/^https?:\/\//.test(ctaUrl)) {
          window.open(ctaUrl, "_blank");
        } else if (mismatch === "switch") {
          switchRoleAndNavigate(ctaUrl);
        } else if (mismatch === "blocked") {
          showRoleRequiredSnackbar(meta.receiverRole);
        } else {
          navigate(ctaUrl);
          closePanel();
        }
      }
    } else if (meta.ctaAction === "copy") {
      // 26 — copy the online-meeting link
      const link =
        notification.display_params?.url ?? notification.param3 ?? "";
      if (link) {
        navigator.clipboard
          .writeText(link)
          .then(() => showSnackbar(t("notifications-ui:linkCopied"), "success"))
          .catch(() => showSnackbar(t("notifications-ui:copyFailed"), "error"));
      }
    } else if (meta.ctaAction === "tos") {
      // The admin app surfaces TOS in a dialog owned by the layout
      emitter.emit("open-tos-update-dialog");
      closePanel();
    }
  };

  const handleSecondaryCtaClick = (notification) => {
    const meta = NOTIFICATION_TYPE_META[notification.notification_id];
    if (meta?.secondaryCtaAction === "navigate" && meta.secondaryCtaRoute) {
      navigate(meta.secondaryCtaRoute);
      closePanel();
    }
    markNotificationRead(notification);
  };

  // Contact info cache: keyed by notification recipient id
  const [contactInfoCache, setContactInfoCache] = useState({});
  const contactInfoCacheRef = useRef(contactInfoCache);
  contactInfoCacheRef.current = contactInfoCache;

  const fetchContactInfo = useCallback(async (notification) => {
    const cacheKey = notification.id;
    if (contactInfoCacheRef.current[cacheKey]) return;

    setContactInfoCache((prev) => ({
      ...prev,
      [cacheKey]: { data: null, loading: true },
    }));

    try {
      const lookup = getContactLookup(notification);
      const response = await apiGetRequest(
        endpoints.getNotificationContactInfo(lookup),
      );
      setContactInfoCache((prev) => ({
        ...prev,
        [cacheKey]: {
          data: response?.data?.contactInfo ?? null,
          loading: false,
        },
      }));
    } catch {
      setContactInfoCache((prev) => ({
        ...prev,
        [cacheKey]: { data: null, loading: false },
      }));
    }
  }, []);

  // Auto-fetch contact info for notifications that need it
  useEffect(() => {
    historyItems.forEach((item) => {
      const meta = NOTIFICATION_TYPE_META[item.notification_id];
      if (meta?.ctaAction === "contact_info") {
        fetchContactInfo(item);
      }
    });
  }, [historyItems, fetchContactInfo]);

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: { xs: "0px", md: "12px" },
        width: "100%",
        height: !isBelowMd ? "100%" : "auto",
        display: "flex",
        flexDirection: "column",
        overflowY: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          px: { xs: "16px", md: "44px" },
          py: { xs: "21px", md: "16px" },
          borderBottom: "1px solid rgba(234, 234, 234, 1)",
        }}
      >
        <Box sx={{ cursor: "pointer", display: { xs: "flex", md: "none" } }}>
          {isHebrew ? (
            <ArrowForwardIcon
              onClick={handleNotificationDrawerClose}
              fontSize="small"
            />
          ) : (
            <ArrowBackIcon
              onClick={handleNotificationDrawerClose}
              fontSize="small"
            />
          )}
        </Box>
        <Typography
          sx={{
            fontSize: { xs: "18px", md: "15px" },
            color: "rgba(72, 72, 72, 1)",
            fontWeight: 500,
            lineHeight: { xs: "100%", md: "22px" },
          }}
        >
          {t("notifications-ui:drawerTitle")}
        </Typography>
        {unreadNotificationsCount > 0 && (
          <Typography
            component="button"
            type="button"
            onClick={handleMarkAllRead}
            sx={{
              marginInlineStart: "auto",
              fontSize: "13px",
              fontWeight: 500,
              color: "rgba(36, 139, 112, 1)",
              background: "none",
              border: "none",
              cursor: "pointer",
              p: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {t("notifications-ui:markAllRead")}
          </Typography>
        )}
      </Box>

      <Box
        ref={listRef}
        className="hide-scrollbar"
        sx={{
          flex: 1,
          width: "100%",
          overflowY: "auto",
          borderBottomRightRadius: { xs: "0px", md: "12px" },
          borderBottomLeftRadius: { xs: "0px", md: "12px" },
        }}
      >
        {!historyLoaded ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              px: "24px",
              py: "48px",
            }}
          >
            <CircularProgress size={20} sx={{ color: "rgba(72,72,72,0.4)" }} />
          </Box>
        ) : historyItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              px: "24px",
              py: "48px",
            }}
          >
            <Typography
              sx={{ fontSize: "14px", color: "rgba(72, 72, 72, 0.4)" }}
            >
              {t("notifications-ui:emptyState")}
            </Typography>
          </Box>
        ) : (
          <>
            {historyItems.map((item, index) => {
              const meta = NOTIFICATION_TYPE_META[item.notification_id];
              const cached = contactInfoCache[item.id];
              const isRead = item.is_read === true || readIds.has(item.id);
              const customCta =
                meta?.ctaAction === "contact_info" ? (
                  <ContactInfoIcons
                    contactInfo={cached?.data ?? null}
                    isLoading={cached?.loading ?? false}
                  />
                ) : undefined;

              return (
                <NotificationItem
                  notification={item}
                  key={`${item.id}-${index}`}
                  isFirstItem={index === 0}
                  isBelowMd={isBelowMd}
                  isRead={isRead}
                  onCtaClick={handleCtaClick}
                  onSecondaryCtaClick={handleSecondaryCtaClick}
                  onMarkRead={markNotificationRead}
                  onToggleRead={handleToggleRead}
                  t={t}
                  customCta={customCta}
                />
              );
            })}
            {hasMoreHistory && (
              <Box
                ref={sentinelRef}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: "16px",
                }}
              >
                <CircularProgress
                  size={16}
                  sx={{
                    color: "rgba(72,72,72,0.4)",
                    opacity: isHistoryLoading ? 1 : 0,
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default Notifications;
